// src/components/horses/HorsesList.tsx

import React, { useEffect, useState } from 'react';
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import {
  PlusCircle,
  Edit2,
  Trash2,
  CheckCircle,
  XCircle,
  UserMinus,
} from 'lucide-react';
import { HorseModal } from './HorseModal';
import { useUserRole } from '../../hooks/useUserRole';
import { ConfirmationModal } from '../common/ConfirmationModal';
import { createNotification } from '../../utils/notifications';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { HorseProfileModal } from './profile/HorseProfileModal';

interface Horse {
  id: string;
  name: string;
  breed: string;
  gender: 'mare' | 'stallion' | 'gelding';
  owner: {
    id: string;
    email: string;
    name?: string;
  };
  dateOfBirth?: string;
  notes?: string;
  coRiders?: {
    [key: string]: {
      id: string;
      email: string;
      name?: string;
      status: 'pending' | 'approved' | 'denied';
      requestedAt: Timestamp;
      updatedAt?: Timestamp;
    };
  };
}

interface OwnerData {
  name?: string;
  email?: string;
}

export const HorsesList = () => {
  const { user } = useAuth();
  const { isManager, isCoRider, loading: isLoading } = useUserRole();
  const { features } = useFeatureFlags();
  const [horses, setHorses] = useState<Horse[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingHorse, setEditingHorse] = useState<Horse | null>(null);
  const [selectedHorse, setSelectedHorse] = useState<Horse | null>(null);
  const [horseToDelete, setHorseToDelete] = useState<Horse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState<string>(''); // Suchzustand hinzufügen

  // Funktion zur Überprüfung, ob der aktuelle Benutzer der Eigentümer eines Pferdes ist
  const isOwner = (horse: Horse) => {
    return user?.uid === horse.owner.id;
  };

  // Funktion zum Abrufen des Benutzernamens aus Firestore
  const fetchUserName = async (userId: string): Promise<string> => {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const data = userDoc.data();
        const name = data?.name || data?.displayName || 'Unbekannt';
        console.log(`Fetched name for user ${userId}: ${name}`); // Debugging
        return name;
      }
      return 'Unbekannt';
    } catch (error) {
      console.error(`Fehler beim Abrufen des Namens für Benutzer ${userId}:`, error);
      return 'Unbekannt';
    }
  };

  useEffect(() => {
    if (!user) return;

    const q = query(collection(db, 'horses'));
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      try {
        const horsesData = await Promise.all(
          snapshot.docs.map(async (docSnapshot) => {
            const data = docSnapshot.data();
            let ownerInfo = {
              id: '',
              email: '',
              name: '',
            };

            if (data.owner && data.owner.id) {
              const ownerRef = doc(db, 'users', data.owner.id);
              const ownerDoc = await getDoc(ownerRef);
              const ownerData = ownerDoc.exists() ? (ownerDoc.data() as OwnerData) : { name: undefined };

              ownerInfo = {
                id: data.owner.id,
                email: data.owner.email,
                name: ownerData?.name || 'Unbekannt',
              };
            } else {
              console.warn(`Owner ID fehlt für Pferd ${docSnapshot.id}`);
              ownerInfo = {
                id: '',
                email: data.owner?.email || 'Unbekannt',
                name: 'Unbekannt',
              };
            }

            let coRiders: Horse['coRiders'] = {};

            if (data.coRiders) {
              const coRiderEntries = await Promise.all(
                Object.entries(data.coRiders).map(async ([key, coRider]) => {
                  const name = await fetchUserName(key);
                  return [
                    key,
                    {
                      ...coRider,
                      name,
                    },
                  ];
                })
              );
              coRiders = Object.fromEntries(coRiderEntries);
            }

            return {
              id: docSnapshot.id,
              ...data,
              owner: ownerInfo,
              coRiders,
            };
          })
        );
        setHorses(horsesData);
        console.log('Loaded horses:', horsesData); // Debugging
        setError(null);
        setLoading(false);
      } catch (err) {
        console.error('Fehler beim Laden der Pferde:', err);
        setError('Pferde konnten nicht geladen werden');
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [user]);

  const handleAddEdit = async (horseData: Omit<Horse, 'id' | 'owner'>) => {
    try {
      if (editingHorse) {
        // Bearbeiten eines bestehenden Pferdes
        const canEdit = isManager || isOwner(editingHorse);
        if (!canEdit) {
          setError('Nur Verwalter oder Besitzer können dieses Pferd bearbeiten.');
          return;
        }

        const updatedHorseData = {
          ...horseData,
          owner: editingHorse.owner, // Behalte den bestehenden Owner bei
        };

        await updateDoc(doc(db, 'horses', editingHorse.id), updatedHorseData);

        // Benachrichtigung an Besitzer bei Änderungen
        await createNotification({
          recipientId: editingHorse.owner.id,
          priority: 'medium',
          metadata: {
            type: 'HORSE_UPDATE',
            horseId: editingHorse.id,
            horseName: horseData.name,
          },
        });
      } else {
        // Hinzufügen eines neuen Pferdes
        if (!user) {
          setError('Benutzer nicht authentifiziert.');
          return;
        }

        const ownerId = user.uid;
        const ownerEmail = user.email || '';
        const ownerName = user.displayName || 'Unbekannt';

        const completeHorseData = {
          ...horseData,
          owner: {
            id: ownerId,
            email: ownerEmail,
            name: ownerName,
          },
          coRiders: {}, // Initialisieren als leeres Objekt
        };

        const docRef = await addDoc(collection(db, 'horses'), completeHorseData);

        // Benachrichtigung an Besitzer bei neuem Pferd
        await createNotification({
          recipientId: completeHorseData.owner.id,
          priority: 'high',
          metadata: {
            type: 'HORSE_CREATED',
            horseId: docRef.id,
            horseName: horseData.name,
          },
        });
      }

      setIsModalOpen(false);
      setEditingHorse(null);
      setError(null);
    } catch (error) {
      console.error('Fehler beim Speichern des Pferdes:', error);
      setError('Pferd konnte nicht gespeichert werden');
    }
  };

  const handleDelete = async () => {
    if (!horseToDelete) return;
    const canDelete = isManager || isOwner(horseToDelete);
    if (!canDelete) {
      setError('Nur Verwalter oder Besitzer können dieses Pferd löschen.');
      return;
    }

    try {
      await deleteDoc(doc(db, 'horses', horseToDelete.id));

      // Benachrichtigung an Besitzer bei Löschung
      await createNotification({
        recipientId: horseToDelete.owner.id,
        priority: 'high',
        metadata: {
          type: 'HORSE_DELETED',
          horseName: horseToDelete.name,
        },
      });

      // Benachrichtigungen an alle genehmigten Co-Rider
      if (horseToDelete.coRiders) {
        await Promise.all(
          Object.entries(horseToDelete.coRiders)
            .filter(([_, coRider]) => coRider.status === 'approved')
            .map(([coRiderId]) =>
              createNotification({
                recipientId: coRiderId,
                priority: 'high',
                metadata: {
                  type: 'HORSE_DELETED',
                  horseName: horseToDelete.name,
                },
              })
            )
        );
      }

      setHorseToDelete(null);
      setError(null);
    } catch (error) {
      console.error('Fehler beim Löschen des Pferdes:', error);
      setError('Pferd konnte nicht gelöscht werden');
    }
  };

  const handleCoRiderRequest = async (horseId: string) => {
    if (!user) return;

    if (!features.coRiderSystem) {
      setError('Das Reitbeteiligungssystem ist derzeit deaktiviert.');
      return;
    }

    try {
      const horseRef = doc(db, 'horses', horseId);
      const horseDoc = await getDoc(horseRef);

      if (!horseDoc.exists()) {
        throw new Error('Pferd nicht gefunden');
      }

      const horseData = horseDoc.data() as Horse;

      // Verhindere, dass Eigentümer sich selbst als Co-Rider hinzufügen
      if (isOwner(horseData)) {
        throw new Error('Eigentümer können sich nicht als Reitbeteiligung registrieren.');
      }

      // Prüfen, ob bereits eine Reitbeteiligung existiert
      if (horseData.coRiders?.[user.uid]) {
        throw new Error('Bereits als Reitbeteiligung registriert oder angefragt');
      }

      // Abrufen des Namens des aktuellen Benutzers aus Firestore
      const userName = await fetchUserName(user.uid);

      // Speichern der Anfrage mit dem Namen aus Firestore
      await updateDoc(horseRef, {
        [`coRiders.${user.uid}`]: {
          id: user.uid,
          email: user.email || '',
          name: userName,
          status: 'pending',
          requestedAt: serverTimestamp() as Timestamp,
        },
      });

      // Benachrichtigung an den Besitzer
      await createNotification({
        recipientId: horseData.owner.id,
        priority: 'high',
        metadata: {
          type: 'HORSE_CORIDER_REQUEST',
          horseId,
          horseName: horseData.name,
          details: {
            requesterId: user.uid,
            requesterName: userName,
          },
        },
      });

      setError(null);
    } catch (error: any) {
      console.error('Fehler bei der Anfrage für Reitbeteiligung:', error);
      setError(error.message || 'Anfrage konnte nicht gesendet werden');
    }
  };

  const handleCoRiderStatusChange = async (
    horse: Horse,
    coRiderId: string,
    newStatus: 'approved' | 'denied'
  ) => {
    try {
      const horseRef = doc(db, 'horses', horse.id);
      const coRider = horse.coRiders?.[coRiderId];

      if (!coRider) return;

      await updateDoc(horseRef, {
        [`coRiders.${coRiderId}.status`]: newStatus,
        [`coRiders.${coRiderId}.updatedAt`]: serverTimestamp() as Timestamp,
      });

      // Benachrichtigung an den Reitbeteiligenden
      await createNotification({
        recipientId: coRiderId,
        priority: 'high',
        metadata: {
          type: 'HORSE_CORIDER_UPDATE',
          horseId: horse.id,
          horseName: horse.name,
          details: {
            status: newStatus,
          },
        },
      });

      if (newStatus === 'denied') {
        // Nach 2 Sekunden den Eintrag komplett entfernen
        setTimeout(async () => {
          const updatedCoRiders = { ...horse.coRiders };
          delete updatedCoRiders[coRiderId];
          await updateDoc(horseRef, { coRiders: updatedCoRiders });
        }, 2000);
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Reitbeteiligungsstatus:', error);
      setError('Status konnte nicht aktualisiert werden');
    }
  };

  const handleRemoveCoRider = async (horse: Horse, coRiderId: string) => {
    try {
      const horseRef = doc(db, 'horses', horse.id);
      const updatedCoRiders = { ...horse.coRiders };
      delete updatedCoRiders[coRiderId];

      await updateDoc(horseRef, { coRiders: updatedCoRiders });

      // Benachrichtigung an den Reitbeteiligenden
      await createNotification({
        recipientId: coRiderId,
        priority: 'high',
        metadata: {
          type: 'HORSE_CORIDER_UPDATE',
          horseId: horse.id,
          horseName: horse.name,
          details: {
            status: 'denied',
          },
        },
      });
    } catch (error) {
      console.error('Fehler beim Entfernen der Reitbeteiligung:', error);
      setError('Reitbeteiligung konnte nicht entfernt werden');
    }
  };

  // Funktion zum Filtern der Pferde basierend auf dem Suchbegriff
  const filteredHorses = horses.filter((horse) => {
    const query = searchQuery.toLowerCase();
    const horseName = horse.name.toLowerCase();
    const ownerName = horse.owner.name?.toLowerCase() || '';
    return horseName.includes(query) || ownerName.includes(query);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Lädt...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Suchleiste hinzufügen */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          Pferde
        </h2>
        {/* Suchinput */}
        <div className="w-full sm:w-1/3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Nach Pferd oder Besitzer suchen..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
        {/* Anzeige der "Pferd hinzufügen"-Schaltfläche für Manager und authentifizierte Benutzer */}
        {(isManager || (!isLoading && !isCoRider)) && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="w-full sm:w-auto flex items-center justify-center px-4 py-2.5 sm:py-2 border border-transparent rounded-md shadow-sm text-base sm:text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
          >
            <PlusCircle className="h-5 w-5 mr-2" />
            Pferd hinzufügen
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
          <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filteredHorses.length > 0 ? (
          filteredHorses.map((horse) => (
            <div
              key={horse.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              <div className="p-4">
                <div className="flex flex-col space-y-4">
                  {/* Horse Header */}
                  <div className="flex items-start justify-between">
                    <div
                      className={`flex-1 min-w-0 ${
                        features.publicProfiles ? 'cursor-pointer' : ''
                      }`}
                      onClick={() =>
                        features.publicProfiles && setSelectedHorse(horse)
                      }
                    >
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                        {horse.name}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {horse.breed}
                      </p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Besitzer: {horse.owner.name}
                      </p>
                    </div>

                    {/* Anzeige der Bearbeiten- und Löschen-Schaltflächen für Manager und Eigentümer */}
                    {(isManager || isOwner(horse)) && (
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => {
                            setEditingHorse(horse);
                            setIsModalOpen(true);
                          }}
                          className="p-2 rounded-lg text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:text-indigo-300 dark:hover:bg-indigo-900/50"
                          title="Pferd bearbeiten"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => setHorseToDelete(horse)}
                          className="p-2 rounded-lg text-red-600 hover:text-red-900 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/50"
                          title="Pferd löschen"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </div>

{/* Co-Riders Section */}
{features.coRiderSystem &&
  (Object.keys(horse.coRiders || {}).length > 0 ||
    horse.owner.id === user?.uid ||
    isManager) && (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
        Reitbeteiligungen
      </h4>
      <div className="space-y-2">
        {Object.entries(horse.coRiders || {})
          .filter(
            ([_, coRider]) => coRider.status !== 'denied'
          )
          .map(([id, coRider]) => (
            <div
              key={id}
              className="flex flex-col bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3"
            >
              {/* Co-Rider Name und Status */}
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-sm truncate dark:text-gray-200">
                  {coRider.name || 'Unbekannt'}
                </span>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    coRider.status === 'approved'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                  }`}
                >
                  {coRider.status === 'approved'
                    ? 'Bestätigt'
                    : 'Ausstehend'}
                </span>
              </div>

              {/* Aktionsbuttons */}
              {(horse.owner.id === user?.uid || isManager) && (
                <div className="flex flex-wrap gap-2">
                  {coRider.status === 'pending' ? (
                    <>
                      <button
                        onClick={() =>
                          handleCoRiderStatusChange(
                            horse,
                            id,
                            'approved'
                          )
                        }
                        className="inline-flex items-center justify-center px-3 py-1.5 rounded-md text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 dark:text-green-200 dark:bg-green-900 dark:hover:bg-green-800"
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Annehmen
                      </button>
                      <button
                        onClick={() =>
                          handleCoRiderStatusChange(
                            horse,
                            id,
                            'denied'
                          )
                        }
                        className="inline-flex items-center justify-center px-3 py-1.5 rounded-md text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 dark:text-red-200 dark:bg-red-900 dark:hover:bg-red-800"
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Ablehnen
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() =>
                        handleRemoveCoRider(horse, id)
                      }
                      className="inline-flex items-center justify-center px-3 py-1.5 rounded-md text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 dark:text-red-200 dark:bg-red-900 dark:hover:bg-red-800"
                    >
                      <UserMinus className="h-4 w-4 mr-1" />
                      Entfernen
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
)}


                  {/* Action Buttons */}
                  <div className="flex items-center justify-between pt-2">
                    {features.publicProfiles && (
                      <button
                        onClick={() => setSelectedHorse(horse)}
                        className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 dark:text-indigo-400 dark:bg-indigo-900/50 dark:hover:bg-indigo-900 rounded-md transition-colors"
                      >
                        Details
                      </button>
                    )}

                    {features.coRiderSystem &&
                      user &&
                      user.uid !== horse.owner.id &&
                      !horse.coRiders?.[user.uid] && (
                        <button
                          onClick={() => handleCoRiderRequest(horse.id)}
                          className="flex-1 sm:flex-none ml-2 inline-flex items-center justify-center px-4 py-2 text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 border border-indigo-600 dark:border-indigo-400 rounded-md"
                        >
                          Reitbeteiligung anfragen
                        </button>
                      )}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center col-span-full">
            <p className="text-gray-500 dark:text-gray-400">
              Keine Pferde gefunden, die den Suchkriterien entsprechen.
            </p>
          </div>
        )}
      </div>

      {/* Modal zum Hinzufügen/Bearbeiten von Pferden */}
      <HorseModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingHorse(null);
        }}
        onSave={handleAddEdit}
        horse={editingHorse}
      />

      {/* Modal für Pferde-Profile */}
      {features.publicProfiles && (
        <HorseProfileModal
          isOpen={!!selectedHorse}
          onClose={() => setSelectedHorse(null)}
          horse={selectedHorse}
          canEdit={
            selectedHorse
              ? isManager || selectedHorse.owner.id === user?.uid
              : false
          }
        />
      )}

      {/* Bestätigungsmodal zum Löschen von Pferden */}
      <ConfirmationModal
        isOpen={!!horseToDelete}
        onClose={() => setHorseToDelete(null)}
        onConfirm={handleDelete}
        title="Pferd löschen"
        message={`Möchten Sie das Pferd "${horseToDelete?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
        confirmLabel="Löschen"
        confirmStyle="danger"
      />
    </div>
  );
};
